import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useOnClickOutside, {
  preventPropagationProps,
} from '../../../hooks/useOnClickOutside';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';

import BURGER_ICON from '../../../images/ic-menu-white.svg';
import BURGER_CLOSE_ICON from '../../../images/ic-menu-close-white.svg';

import { HEADER_BURGER_MENU_BUTTON } from '../../../../automationHooks.json';

const propTypes = {
  headerHeight: PropTypes.number,
  topBarOffset: PropTypes.number,
  children: PropTypes.node.isRequired,
};

const defaultProps = {
  headerHeight: 0,
  topBarOffset: 0,
};

const BurgerButtonContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: ${props =>
    props.hasOutsideComponents ? 'space-between' : 'flex-end'};
  height: ${props => props.headerHeight}px;
  margin-left: ${props => props.theme.spacing(4)};
`;

const BurgerButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  padding: ${props => `${props.theme.spacing(1)} ${props.theme.spacing(2)}`};
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;

  &:hover,
  &:focus {
    border-bottom-color: ${props => props.theme.colors.siteHeader.linkActive};
  }

  ${props =>
    props.isOpen &&
    `
    border-bottom-color: ${props.theme.colors.siteHeader.linkActive};
  `}

  ${props => props.theme.query.lg} {
    padding: ${props => props.theme.spacing(2)};
  }
`;

const Overlay = styled.div.attrs(props => ({
  'aria-hidden': !props.isOpen,
}))`
  display: ${props => (props.isOpen ? 'block' : 'none')};
  position: fixed;
  top: ${props => props.offset}px;
  right: ${props => (props.isOpen ? 0 : '-100vw')};
  bottom: 0;
  left: 0;
  background-color: rgba(38, 50, 56, 0.64);
  z-index: 1;
`;

const Drawer = styled.div.attrs(props => ({
  'aria-hidden': !props.isOpen,
}))`
  position: fixed;
  top: ${props => props.offset}px;
  bottom: 0;
  right: 0;
  width: 300px;
  padding: ${props => props.theme.spacing(4)};
  color: ${props => props.theme.colors.common.textSecondary};
  background-color: #fff;
  z-index: 2;
`;

const BurgerMenu = ({ headerHeight, topBarOffset, children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const toggleIsOpen = () => setIsOpen(prevState => !prevState);

  const childrenArray = React.Children.toArray(children);

  const insideComponents = childrenArray
    .filter(child => child.props.showInsideDrawer)
    .map(child => React.cloneElement(child, { isColumn: true, headerHeight }));

  const outsideComponents = childrenArray
    .filter(child => !child.props.showInsideDrawer)
    .map(child => React.cloneElement(child, { headerHeight }));

  const hasDrawerChildren = childrenArray.some(
    child => child.props.showInsideDrawer
  );

  const burgerMenuContentRef = useBodyScrollLock(isOpen);

  useOnClickOutside(burgerMenuContentRef, () => setIsOpen(false));

  return (
    <BurgerButtonContainer
      headerHeight={headerHeight}
      hasOutsideComponents={Boolean(outsideComponents.length)}
      ref={burgerMenuContentRef}
      {...preventPropagationProps()}
    >
      {outsideComponents}

      {hasDrawerChildren && (
        <>
          <BurgerButton
            onClick={toggleIsOpen}
            isOpen={isOpen}
            data-hook={HEADER_BURGER_MENU_BUTTON.hook}
            aria-label="Toggle menu"
            aria-controls="burger-menu"
          >
            {isOpen ? (
              <img src={BURGER_CLOSE_ICON} alt="" />
            ) : (
              <img src={BURGER_ICON} alt="" />
            )}
          </BurgerButton>
          <Overlay isOpen={isOpen} offset={headerHeight + topBarOffset}>
            <Drawer
              id="burger-menu"
              isOpen={isOpen}
              offset={headerHeight + topBarOffset}
            >
              {isOpen && insideComponents}
            </Drawer>
          </Overlay>
        </>
      )}
    </BurgerButtonContainer>
  );
};

BurgerMenu.propTypes = propTypes;

BurgerMenu.defaultProps = defaultProps;

export default BurgerMenu;
